import { useTheme } from '@mui/material';
import { Chart as ChartJS, ChartOptions, InteractionItem } from 'chart.js';
import convert from 'color-convert';
import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  selectCurrHiddenColorBuckets,
  setCurrHiddenColorBuckets,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  selectHSVSaturation,
  selectHSVValue,
  selectUseCustomHSVSaturation,
  selectUseCustomHSVValue,
} from '../../map/mapSlice';
import useScanData from '../../scanDataApi/hooks/useScanData';
import {
  ValidPointsDropdownOptions,
  setShowValidPointsDropdown,
} from '../../scanDataApi/scanDataSlice';
import { useChartFeatureMeasureLabels } from './useChartLabels';

export const useColorDistributionChart = (
  chart?: ChartJS<'bar', number[], unknown> | null,
  plugins?: ChartOptions['plugins']
) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { colorsDistribution } = useScanData();
  const HSVSaturation = useAppSelector(selectHSVSaturation);
  const HSVValue = useAppSelector(selectHSVValue);
  const useCustomHSVSaturation = useAppSelector(selectUseCustomHSVSaturation);
  const useCustomHSVValue = useAppSelector(selectUseCustomHSVValue);
  const currHiddenColorBuckets = useAppSelector(selectCurrHiddenColorBuckets);

  const hideAll = useCallback(() => {
    if (!colorsDistribution.data) return;
    dispatch(
      setCurrHiddenColorBuckets(colorsDistribution.data.map((el) => el.h))
    );
    dispatch(
      setShowValidPointsDropdown(ValidPointsDropdownOptions.showValidPoints)
    );
  }, [dispatch, colorsDistribution.data]);

  const showAll = useCallback(() => {
    dispatch(setCurrHiddenColorBuckets([]));
    dispatch(
      setShowValidPointsDropdown(ValidPointsDropdownOptions.showAllPoints)
    );
  }, [dispatch]);

  const newHex = useCallback(
    (h: number, s: number, v: number) =>
      convert.hsv.hex([
        h,
        useCustomHSVSaturation ? HSVSaturation : s,
        useCustomHSVValue ? HSVValue : v,
      ]),
    [useCustomHSVSaturation, useCustomHSVValue, HSVSaturation, HSVValue]
  );
  // Get the x and y axis labels for the current chart feature and dimension
  const labelResult = useChartFeatureMeasureLabels();

  const datasets = useMemo(
    () =>
      !colorsDistribution.data
        ? []
        : [
            {
              label: 'interval',
              data:
                colorsDistribution.data.map(({ interval_value }) =>
                  Number(interval_value)
                ) || [],
              fill: false,
              backgroundColor:
                colorsDistribution.data.map((el) =>
                  currHiddenColorBuckets.includes(el.h)
                    ? `#${newHex(el.h, el.s, el.v)}26`
                    : // 15% opacity     --------    ^^
                      `#${newHex(el.h, el.s, el.v)}`
                ) || theme.colors.common.purple,
              rawData: colorsDistribution.data,
            },
          ],
    [colorsDistribution, currHiddenColorBuckets, theme, newHex]
  );

  const data = useMemo(
    () => ({
      datasets,
      labels: !colorsDistribution.data
        ? []
        : colorsDistribution.data.map(() => ``),
      rawData: colorsDistribution.data,
    }),
    [colorsDistribution, datasets]
  );

  // set hidden buckets on map
  const clickHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (elements: InteractionItem[]) => {
      if (chart) {
        elements.forEach((e) => {
          if (chart.data.datasets.length > 0) {
            const bucket = (chart.data.datasets as typeof datasets)[
              e.datasetIndex
            ].rawData[e.index].h;
            if (currHiddenColorBuckets.includes(bucket)) {
              dispatch(
                setCurrHiddenColorBuckets([
                  ...currHiddenColorBuckets.filter((el) => el !== bucket),
                ])
              );
            } else {
              dispatch(
                setCurrHiddenColorBuckets([...currHiddenColorBuckets, bucket])
              );
            }
          }
        });
        chart.update();
      }
    },
    [chart, currHiddenColorBuckets, dispatch]
  );

  // JUST A POSSIBLE FEATURE, BUT PRODUCES A BIT LAG ON MAP POINTS RERENDER
  // // set hover points when the mouse hovers over a bar chart item
  // const hoverHandler = useCallback(
  //   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //   (elements: InteractionItem[]) => {

  //     if (chart) {
  //       elements.forEach((e) => {
  //         if (
  //           chart.data.datasets.length > 0
  //         ) {
  //           const bucket = (chart.data.datasets as typeof datasets)[e.datasetIndex].rawData[e.index].h
  //           dispatch(setCurrHighlightedColorBucket(bucket))
  //         }
  //       });
  //       chart.update();
  //     }
  //   },
  //   [chart, dispatch]
  // );

  // reset hover fields when the mouse leaves a bar chart item
  // const leaveHandler = useCallback(() => {
  //   dispatch(
  //     setCurrHighlightedColorBucket(null)
  //   );
  // }, [dispatch]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      ...plugins,
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        ticks: {
          color: theme.colors.common.white,
        },
        grid: {
          display: true,
          color: theme.colors.darkTheme.grey.muted,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.yLabel : '',
          color: theme.colors.common.white,
        },
      },
      x: {
        ticks: {
          display: false, // for color distribution chart, we don't need x axis ticks
          color: theme.colors.common.white,
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.xLabel : '',
          color: theme.colors.common.white,
          padding: {
            // for color distribution chart, since we don't have x axis ticks or values, we can remove the title padding so it's closer to the chart
            top: 3,
            bottom: 0,
            left: 0,
            right: 0,
          },
        },
      },
    },
    events: ['click' /*'mousemove', 'mouseout'*/] as Array<
      keyof HTMLElementEventMap
    >,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // onHover: (chartInstance: any, elements: any) => {
    //   if (elements.length > 0) {
    //     hoverHandler(elements);
    //   } else {
    //     leaveHandler();
    //   }
    // },
    // onLeave: () => {
    //   leaveHandler();
    // },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick: (chartInstance: any, elements: any) => {
      clickHandler(elements);
    },
  };

  const result = useMemo(
    () => ({
      data,
      options,
      isLoading: colorsDistribution.isLoading,
      hideAll,
      showAll,
    }),
    [data, options, colorsDistribution]
  );

  return result;
};
